var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import 'isomorphic-fetch';
import { v4 as uuidv4 } from 'uuid';
import ApiError from '~/errors/api';
import { keysToCamel } from '~/utils/toCamelCase';
import { keysToSnake } from '~/utils/toSnakeCase';
export default class BaseApi {
    constructor(route) {
        var _a;
        const apiUrl = ((_a = window === null || window === void 0 ? void 0 : window.RG_CONFIG) === null || _a === void 0 ? void 0 : _a.API_URL) || '';
        this.route = route;
        this.url = `${apiUrl}${route}`;
        this.useMock = window.location.search.includes('MOCK_E2E');
    }
    route(route) {
        return new BaseApi(route);
    }
    post(payload_1) {
        return __awaiter(this, arguments, void 0, function* (payload, redirectToErrorPage = true) {
            if (this.useMock) {
                if (!payload.id) {
                    payload.id = uuidv4();
                }
                return payload;
            }
            let response;
            try {
                response = yield fetch(this.url, {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(keysToSnake(payload)),
                });
            }
            catch (error) {
                if (redirectToErrorPage) {
                    const apiError = new ApiError(`Failed to POST to '${this.route}': ${error.message}`);
                    const info = JSON.stringify({ payload });
                    window.routeToErrorPage(apiError, info);
                }
            }
            if (response && response.status === 200) {
                const res = yield response.json();
                if (res.query_info) {
                    return {
                        queryInfo: keysToCamel(res.query_info),
                        data: keysToCamel(res.data),
                    };
                }
                return keysToCamel(res.data);
            }
        });
    }
    getById(primaryId_1) {
        return __awaiter(this, arguments, void 0, function* (primaryId, redirectToErrorPage = true, queryParamString = '') {
            if (this.useMock) {
                return { id: primaryId };
            }
            let url = `${this.url}/${primaryId}`;
            if (queryParamString !== '') {
                url = url + queryParamString;
            }
            let response;
            try {
                response = yield fetch(url, {
                    method: 'GET',
                    mode: 'cors',
                    headers: {
                        Accept: 'application/json',
                    },
                    credentials: 'include',
                });
            }
            catch (error) {
                if (redirectToErrorPage) {
                    const apiError = new ApiError(`Failed to GET '${this.route}' by ID '${primaryId}': ${error.message}`);
                    const info = JSON.stringify({ primaryId });
                    window.routeToErrorPage(apiError, info);
                }
            }
            if (response && response.status === 200) {
                const res = yield response.json();
                return keysToCamel(res.data);
            }
        });
    }
    get() {
        return __awaiter(this, arguments, void 0, function* (queryParams = {}, redirectToErrorPage = true) {
            if (this.useMock) {
                return [];
            }
            queryParams = keysToSnake(queryParams);
            let queryString = Object.keys(queryParams).reduce((total, key) => {
                return total + `${key}=${encodeURIComponent(queryParams[key])}&`;
            }, '');
            if (queryString.endsWith('&')) {
                queryString = queryString.slice(0, -1);
            }
            if (queryString.length) {
                queryString = '?' + queryString;
            }
            const response = yield fetch(`${this.url}${queryString}`, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    Accept: 'application/json',
                },
                credentials: 'include',
            });
            if (!response || response.status !== 200) {
                return;
            }
            const res = yield response.json();
            if (res.status_code === 206) {
                queryParams = keysToCamel(queryParams);
                queryParams.blockIndex = queryParams.blockIndex ? queryParams.blockIndex + 1 : 1;
                return res.queryInfo
                    ? {
                        queryInfo: keysToCamel(res.query_info),
                        data: [
                            ...keysToCamel(res.data),
                            ...yield this.get(BaseApi.accessToken, queryParams, redirectToErrorPage),
                        ],
                    }
                    : [
                        ...keysToCamel(res.data),
                        ...yield this.get(BaseApi.accessToken, queryParams, redirectToErrorPage),
                    ];
            }
            return res.queryInfo
                ? {
                    queryInfo: keysToCamel(res.query_info),
                    data: keysToCamel(res.data),
                }
                : keysToCamel(res.data);
        });
    }
    put(payload_1) {
        return __awaiter(this, arguments, void 0, function* (payload, redirectToErrorPage = true) {
            if (this.useMock) {
                if (!payload.id) {
                    payload.id = uuidv4();
                }
                return payload;
            }
            let response;
            try {
                response = yield fetch(this.url, {
                    method: 'PUT',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(keysToSnake(payload)),
                });
            }
            catch (error) {
                if (redirectToErrorPage) {
                    const apiError = new ApiError(`Failed to PUT to '${this.route}': ${error.message}`);
                    const info = JSON.stringify({ payload });
                    window.routeToErrorPage(apiError, info);
                }
            }
            if (response && response.status === 200) {
                const res = yield response.json();
                return keysToCamel(res.data);
            }
        });
    }
    update(primaryId_1, payload_1) {
        return __awaiter(this, arguments, void 0, function* (primaryId, payload, redirectToErrorPage = true) {
            if (this.useMock) {
                return Object.assign(Object.assign({}, payload), { id: primaryId });
            }
            if (payload.id) {
                delete payload.id;
            }
            let response;
            try {
                response = yield fetch(`${this.url}/${primaryId}`, {
                    method: 'PATCH',
                    mode: 'cors',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(keysToSnake(payload)),
                });
            }
            catch (error) {
                if (redirectToErrorPage) {
                    const apiError = new ApiError(`Failed to PATCH '${this.route}': ${error.message}`);
                    const info = JSON.stringify({ primaryId, payload });
                    window.routeToErrorPage(apiError, info);
                }
            }
            if (response && response.status === 200) {
                const res = yield response.json();
                return keysToCamel(res.data);
            }
        });
    }
    deleteById(primaryId_1) {
        return __awaiter(this, arguments, void 0, function* (primaryId, redirectToErrorPage = true) {
            if (this.useMock) {
                return true;
            }
            let response;
            try {
                response = yield fetch(`${this.url}/${primaryId}`, {
                    method: 'DELETE',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });
            }
            catch (error) {
                if (redirectToErrorPage) {
                    const apiError = new ApiError(`Failed to DELETE by '${this.route}' ID '${primaryId}': ${error.message}`);
                    const info = JSON.stringify({ primaryId });
                    window.routeToErrorPage(apiError, info);
                }
            }
            return (response && response.status === 200);
        });
    }
    delete(queryParams_1) {
        return __awaiter(this, arguments, void 0, function* (queryParams, redirectToErrorPage = true) {
            if (this.useMock) {
                return true;
            }
            queryParams = keysToSnake(queryParams);
            let queryString = Object.keys(queryParams).reduce((total, key) => {
                return total + `${key}=${queryParams[key]}&`;
            }, '');
            if (queryString.endsWith('&')) {
                queryString = queryString.slice(0, -1);
            }
            let response;
            try {
                response = yield fetch(`${this.url}?${queryString}`, {
                    method: 'DELETE',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });
            }
            catch (error) {
                if (redirectToErrorPage) {
                    const apiError = new ApiError(`Failed to DELETE '${this.route}': ${error.message}`);
                    const info = JSON.stringify(queryParams);
                    window.routeToErrorPage(apiError, info);
                }
            }
            return (response && response.status === 200);
        });
    }
}
